import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card } from "react-bootstrap";
import { Line } from "react-chartjs-2";
import { getPreciosMulti, getRangosHistorico } from "../services/api";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
  Filler,
} from "chart.js";
import "../styles/MultiPairChart.css";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
  Filler
);

const MAX_SERIES = 8;
const MAX_LABELS = 400;

const PALETA = [
  "#53ffc0", "#ffb84d", "#6ba8ff", "#ff7ba8",
  "#c792ff", "#4ddbc4", "#ff8e8e", "#a3e635",
];

const RANGOS_POR_DEFECTO = [
  { clave: "1h", horas: 1 },
  { clave: "4h", horas: 4 },
  { clave: "12h", horas: 12 },
  { clave: "24h", horas: 24 },
  { clave: "3d", horas: 72 },
  { clave: "7d", horas: 168 },
  { clave: "30d", horas: 720 },
];

const TF_ETIQUETAS = { "5m": "5M", "1h": "1H", "4h": "4H", "12h": "12H", "24h": "24H" };

function tendenciaClase(tf) {
  const t = String(tf || "").toUpperCase();
  if (t.includes("ALCISTA_FUERTE")) return "t-alcista-fuerte";
  if (t.includes("ALCISTA")) return "t-alcista";
  if (t.includes("BAJISTA_FUERTE")) return "t-bajista-fuerte";
  if (t.includes("BAJISTA")) return "t-bajista";
  return "t-neutral";
}

const MultiPairChart = ({ availablePairs = [], currentUser = null }) => {
  const [rango, setRango] = useState("24h");
  const [seleccion, setSeleccion] = useState([]);
  const [usarTodos, setUsarTodos] = useState(true);
  const [compararPct, setCompararPct] = useState(true);
  const [datos, setDatos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [rangosDisponibles, setRangosDisponibles] = useState(RANGOS_POR_DEFECTO);
  const requestSeq = useRef(0);

  const simbolosDisponibles = useMemo(
    () =>
      (availablePairs || [])
        .map((p) => (typeof p === "string" ? p : p?.symbol))
        .filter(Boolean),
    [availablePairs]
  );

  useEffect(() => {
    getRangosHistorico()
      .then((r) => {
        if (Array.isArray(r) && r.length) setRangosDisponibles(r);
      })
      .catch(() => {});
  }, []);

  const horasActivas = useMemo(() => {
    const r = rangosDisponibles.find((x) => x.clave === rango);
    return r ? r.horas : 24;
  }, [rango, rangosDisponibles]);

  const cargar = useCallback(async () => {
    const pedido = usarTodos ? [] : seleccion;
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setLoading(true);
    try {
      const res = await getPreciosMulti(pedido, horasActivas);
      if (seq !== requestSeq.current) return;
      setDatos(Array.isArray(res?.symbols) ? res.symbols : []);
      setError(null);
      setLastUpdate(new Date());
    } catch (e) {
      if (seq !== requestSeq.current) return;
      setError(e?.response?.data?.detail?.message || e?.message || "Error cargando datos");
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [usarTodos, seleccion, horasActivas]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const claveSimbolos = simbolosDisponibles.join(",");

  useEffect(() => {
    setUsarTodos(true);
    setSeleccion([]);
  }, [currentUser, claveSimbolos]);

  const togglePar = (sym) => {
    setUsarTodos(false);
    setSeleccion((prev) => {
      if (prev.includes(sym)) {
        const next = prev.filter((s) => s !== sym);
        return next.length ? next : prev;
      }
      if (prev.length >= MAX_SERIES) return prev;
      return [...prev, sym];
    });
  };

  const series = useMemo(() => {
    const base = datos.slice(0, MAX_SERIES);
    if (!base.length) return { labels: [], datasets: [] };

    // Cada símbolo llega con su propia rejilla de timestamps ($bucketAuto corre
    // por símbolo), así que NO comparten marcas. Tomar el Set de todas las
    // marcas daba ~6.500 labels y 8 datasets × 6.500 puntos (~52k en el chart,
    // casi todos null) que es lo que hacia lento el pintado. Ahora se unifica
    // sobre una rejilla comun recortada a MAX_LABELS y cada serie toma el
    // punto mas cercano (busqueda binaria) en vez de exigir coincidencia exacta.
    const grids = base.map((s) =>
      (s.puntos || [])
        .filter((p) => p && p.t != null && p.p != null)
        .map((p) => [p.t, Number(p.p)])
        .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    );

    const marcas = [...new Set(grids.flatMap((g) => g.map((x) => x[0])))].sort();
    if (!marcas.length) return { labels: [], datasets: [] };

    const paso = Math.max(1, Math.ceil(marcas.length / MAX_LABELS));
    const unicos = marcas.filter((_, i) => i % paso === 0);
    if (unicos[unicos.length - 1] !== marcas[marcas.length - 1]) unicos.push(marcas[marcas.length - 1]);

    const masCercano = (g, t) => {
      if (!g.length) return null;
      let lo = 0;
      let hi = g.length - 1;
      let res = -1;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (g[mid][0] >= t) {
          res = mid;
          hi = mid - 1;
        } else {
          lo = mid + 1;
        }
      }
      if (res < 0) return g[0][1];
      if (res > 0) {
        const antes = g[res - 1][0];
        const ahora = g[res][0];
        return t - antes <= ahora - t ? g[res - 1][1] : g[res][1];
      }
      return g[0][1];
    };

    return {
      labels: unicos,
      datasets: base.map((s, i) => {
        const g = grids[i];
        const vals = unicos.map((t) => masCercano(g, t));
        const ref = vals.find((v) => v !== null && v !== 0) ?? null;
        const data = compararPct
          ? vals.map((v) => (v === null || !ref ? null : ((v - ref) / ref) * 100))
          : vals;
        const color = PALETA[i % PALETA.length];
        return {
          label: s.symbol,
          data,
          borderColor: color,
          backgroundColor: color + "22",
          borderWidth: 1.8,
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0.2,
          spanGaps: true,
          fill: false,
        };
      }),
    };
  }, [datos, compararPct]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: {
          display: true,
          labels: {
            color: "#95aebb",
            font: { size: 11, weight: "700" },
            usePointStyle: true,
            boxWidth: 8,
          },
        },
        tooltip: {
          backgroundColor: "rgba(2, 10, 20, 0.94)",
          titleColor: "#5bffc8",
          bodyColor: "#f0fbff",
          borderColor: "rgba(83, 255, 192, 0.3)",
          borderWidth: 1,
          callbacks: {
            label: (ctx) => {
              const v = ctx.raw;
              if (v === null || v === undefined) return `${ctx.dataset.label}: sin datos`;
              return compararPct
                ? `${ctx.dataset.label}: ${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}%`
                : `${ctx.dataset.label}: $${Number(v).toFixed(4)}`;
            },
          },
        },
      },
      scales: {
        y: {
          grid: { color: "rgba(0, 255, 170, 0.08)" },
          ticks: {
            color: "#8ea5b2",
            font: { size: 10 },
            callback: (v) => (compararPct ? `${Number(v).toFixed(1)}%` : `$${Number(v).toFixed(2)}`),
          },
        },
        x: {
          grid: { color: "rgba(0, 255, 170, 0.05)" },
          ticks: {
            color: "#8ea5b2",
            font: { size: 9 },
            maxTicksLimit: 8,
            maxRotation: 0,
            autoSkip: true,
          },
        },
      },
    }),
    [compararPct]
  );

  const tabla = useMemo(
    () =>
      [...datos]
        .sort((a, b) => (b.variacion_pct ?? -Infinity) - (a.variacion_pct ?? -Infinity))
        .map((s, i) => ({ ...s, color: PALETA[i % PALETA.length] })),
    [datos]
  );

  const resumenMercado = useMemo(() => {
    const alcistas = datos.filter((s) => (s.variacion_pct ?? 0) > 0).length;
    return { alcistas, total: datos.length };
  }, [datos]);

  return (
    <Card className="ind-chart-card mpc-card">
      <Card.Header className="ind-chart-header">
        <div className="ind-chart-header-row">
          <div>
            <h3 className="ind-chart-title">Comparador de pares</h3>
            <small className="mpc-subtitle">
              Variación y tendencias por timeframe para decidir entradas
            </small>
          </div>
          <span className="ind-chart-time">
            {lastUpdate ? `Actualizado: ${lastUpdate.toLocaleTimeString()}` : "Actualizando..."}
          </span>
        </div>
      </Card.Header>

      <Card.Body className="ind-chart-body">
      <div className="mpc-controls">
        <div className="mpc-rangos" role="tablist" aria-label="Rango temporal">
          {rangosDisponibles.map((r) => (
            <button
              key={r.clave}
              type="button"
              role="tab"
              aria-selected={rango === r.clave}
              className={`mpc-rango-btn ${rango === r.clave ? "active" : ""}`}
              onClick={() => setRango(r.clave)}
            >
              {r.clave}
            </button>
          ))}
        </div>

        <div className="mpc-toggles">
          <label className="mpc-switch">
            <input
              type="checkbox"
              checked={usarTodos}
              onChange={(e) => setUsarTodos(e.target.checked)}
            />
            <span>Todos ({simbolosDisponibles.length})</span>
          </label>
          <label className="mpc-switch">
            <input
              type="checkbox"
              checked={compararPct}
              onChange={(e) => setCompararPct(e.target.checked)}
            />
            <span>Comparar en %</span>
          </label>
          <button
            type="button"
            className="mpc-refresh"
            onClick={cargar}
            disabled={loading}
            title="Recargar"
          >
            {loading ? "⏳" : "🔄"}
          </button>
        </div>
      </div>

      {/* ===== Chips de selección (uno, varios o todos) ===== */}
      {simbolosDisponibles.length > 0 && (
        <div className="mpc-chips">
          {simbolosDisponibles.map((s) => {
            const activo = usarTodos || seleccion.includes(s);
            return (
              <button
                key={s}
                type="button"
                className={`mpc-chip ${activo ? "active" : ""}`}
                onClick={() => togglePar(s)}
                title={usarTodos ? "Todos activos — clic para seleccionar" : "Alternar par"}
              >
                {s.replace("_USDT", "").replace("_PERP", "")}
              </button>
            );
          })}
        </div>
      )}

      {error && <div className="mpc-error">❌ {error}</div>}

      {/* ===== Gráfico ===== */}
      <div className="mpc-plot-wrap">
        {series.labels.length ? (
          <Line data={series} options={chartOptions} />
        ) : (
          <div className="mpc-empty">
            {loading ? "⏳ Cargando histórico..." : "Sin datos para el rango seleccionado"}
          </div>
        )}
      </div>

      {/* ===== Tabla comparativa ===== */}
      {tabla.length > 0 && (
        <div className="mpc-table-wrap">
          <div className="mpc-table-head">
            <span>Par</span>
            <span>Var. {rango}</span>
            <span>Precio</span>
            <span>Tendencias</span>
          </div>
          {tabla.map((s) => (
            <div className="mpc-row" key={s.symbol}>
              <span className="mpc-par">
                <i style={{ background: s.color }} />
                {s.symbol}
              </span>
              <span
                className={`mpc-var ${
                  (s.variacion_pct ?? 0) >= 0 ? "pos" : "neg"
                }`}
              >
                {(s.variacion_pct ?? 0) >= 0 ? "▲" : "▼"}{" "}
                {Math.abs(s.variacion_pct ?? 0).toFixed(2)}%
              </span>
              <span className="mpc-precio">${Number(s.ultimo_precio ?? 0).toFixed(4)}</span>
              <span className="mpc-tfs">
                {Object.keys(TF_ETIQUETAS).map((tf) => (
                  <span
                    key={tf}
                    className={`mpc-tf ${tendenciaClase((s.tendencias || {})[tf])}`}
                    title={`${tf.toUpperCase()}: ${(s.tendencias || {})[tf] || "?"}`}
                  >
                    {TF_ETIQUETAS[tf]}
                  </span>
                ))}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="mpc-foot">
        {resumenMercado.total > 0 && (
          <span>
            {resumenMercado.alcistas} de {resumenMercado.total} pares en verde en{" "}
            {rango}
          </span>
        )}
        {usarTodos && datos.length >= MAX_SERIES && (
          <span>Mostrando los {MAX_SERIES} primeros por claridad visual</span>
        )}
      </div>
      </Card.Body>
    </Card>
  );
};

export default React.memo(MultiPairChart);
